// Proximity-aware due-date formatter. Maps an ISO due date + deliverable
// status to a short label and tone band so table cells and summary tiles
// can render the same proximity language consistently (brief §9.8).
//
// The proximity countdown (overdue / today / in Nd) only applies while
// the supplier still owes the original delivery — i.e. `not-started` or
// the derived `overdue` (past due, nothing submitted yet). Once a
// submission exists the clock stops:
//   - submitted / under-review / conditionally-accepted / accepted, and
//     the derived `late` (submitted AFTER the due date — the backend
//     collapses a late submission's stored `submitted` into this single
//     derived value) render a muted "submitted" tag — delivery happened;
//     lateness at submission is the slip tag's job, not a live countdown.
//     Without `late` here, a late-submitted occurrence kept counting
//     "Nd overdue" from today even though it had already been delivered.
//   - revision-requested / awaiting-resubmission are supplier-rework
//     queues (their own queue, not overdue — mirrors
//     deriveDeliverableStatus), and rejected / closed are terminal:
//     all four render the bare muted date; the status pill alongside
//     carries the state.

import type { DeliverableDerivedStatus } from "../../../../types";
import { formatDayMonth } from "../../../../utils/formatShortDate";

export type DueDateTone = "danger" | "warning" | "neutral" | "muted";

export interface FormatDueDateResult {
  text: string;
  tone: DueDateTone;
}

const SUBMITTED_STATUSES: ReadonlySet<DeliverableDerivedStatus> = new Set([
  "submitted",
  "under-review",
  "conditionally-accepted",
  "accepted",
  // Derived: submitted after the due date. Clock stops at submission —
  // the slip tag + 'Late' pill carry the lateness.
  "late",
]);

const DATE_ONLY_STATUSES: ReadonlySet<DeliverableDerivedStatus> = new Set([
  "revision-requested",
  "awaiting-resubmission",
  "rejected",
  "closed",
]);

// Whole-day difference in UTC — avoids DST off-by-ones and negative-zero
// around midnight boundaries.
function daysBetweenUTC(from: Date, toIso: string): number {
  const a = Date.UTC(
    from.getUTCFullYear(),
    from.getUTCMonth(),
    from.getUTCDate(),
  );
  const due = new Date(`${toIso}T00:00:00Z`);
  const b = Date.UTC(
    due.getUTCFullYear(),
    due.getUTCMonth(),
    due.getUTCDate(),
  );
  return Math.round((b - a) / 86_400_000);
}

export function formatDueDate(
  dueDate: string,
  status: DeliverableDerivedStatus,
  today: Date = new Date(),
): FormatDueDateResult {
  const dateStr = formatDayMonth(dueDate);

  if (SUBMITTED_STATUSES.has(status)) {
    return { text: `${dateStr} · submitted`, tone: "muted" };
  }

  if (DATE_ONLY_STATUSES.has(status)) {
    return { text: dateStr, tone: "muted" };
  }

  const days = daysBetweenUTC(today, dueDate);

  if (days < 0) {
    return { text: `${dateStr} · ${Math.abs(days)}d overdue`, tone: "danger" };
  }
  if (days === 0) {
    return { text: `${dateStr} · today`, tone: "warning" };
  }
  if (days <= 7) {
    return { text: `${dateStr} · in ${days}d`, tone: "warning" };
  }
  if (days <= 30) {
    const weeks = Math.round(days / 7);
    const label = weeks === 1 ? "week" : "weeks";
    return { text: `${dateStr} · in ${weeks} ${label}`, tone: "neutral" };
  }
  return { text: dateStr, tone: "muted" };
}
